import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { useForm, useWatch } from 'react-hook-form';
import i18next from 'i18next';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';

// Actions
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Form
import { buildSchema } from '@commons/GenericForm';
import { getInputs } from './utils/form';
import { yupResolver } from '@hookform/resolvers/yup';
import WhiteCardForm from '@commons/WhiteCardForm';

// Components
import { Button } from '@commons/utils/styledLibraryComponents';
import Text, { ENUM_FONTS } from '@commons/Text';

// Commons
import {
  addDateSuffixToFileName,
  downloadFile,
  downloadFileFromUrl,
  encodeFileNameInUrl,
  getFileNameFromUrl,
  truncateFileNameInTheMiddle,
} from '@commons/utils/fileUtils';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';

// Selectors
import { getAuthorizedActions } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';

// Services
import { upload as uploadService } from '@services/upload';
import invoiceService from '@services/invoice';

import { INVOICE_STATUS } from '@src/routes/invoice/commons/constants';

// Styles
import {
  Icon,
  ModalBackground,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  TitleWithIcon,
} from './styledComponents';

export const DeliveryInvoiceModal = (props) => {
  const {
    // Props
    close,
    isReadOnly,
    orderId,
    storeId,
    supplierId,
    // Dispatch methods / props
    client: { clientId },
    currency,
    pageLoaded,
    pageLoading,
    showErrorMessage,
    showSuccessMessage,
  } = props;

  const [invoiceControl, setInvoiceControl] = useState({});
  const intervalRef = useRef(null);

  // Form states
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [inputs, setInputs] = useState(getInputs({ currency }));
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isSaveAlreadyTriggered, setIsSaveAlreadyTriggered] = useState(false);

  const uploadInvoice = (file) => {
    setFile(file);
    setFileName(file.name);

    // Add a suffix to the file name to ensure uniqueness (for instance 231224080955 stands for 23/12/2024 08:09:55)
    const updatedFileName = addDateSuffixToFileName(file.name, new Date());
    const updatedFile = new File([file], updatedFileName, { type: file.type });
    setFile(updatedFile);

    form.setValue('fileName', file.name);
  };

  const deleteInvoice = () => {
    setFile(null);
    setFileName('');
    setInvoiceControl({
      ...invoiceControl,
      link: null,
    });
    form.setValue('fileName', '');
  };

  /**
   * Generic callback to download either the file being uploaded or the one already associated with the invoice control
   */
  const downloadInvoice = () => {
    if (file) {
      return downloadFileBeingUploaded();
    }

    if (invoiceControl.link) {
      return downloadInvoiceLink();
    }

    showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FILE_DOWNLOAD_ERROR'));
  };

  const form = useForm({
    defaultValues: {},
    resolver: yupResolver(
      buildSchema(
        getInputs({
          invoiceControl,
          isReadOnly,
          fileBeingUploadedName: fileName,
          currency,
          uploadInvoice,
          deleteInvoice,
          downloadInvoice,
        }),
      ),
    ),
  });

  const formFields = useWatch({
    control: form.control,
  });

  // Fetch invoice info
  useEffect(() => {
    if (!orderId) {
      return;
    }

    getICLByOrderId(orderId);
  }, [orderId]);

  useEffect(() => {
    // Update file input actions
    setInputs(
      getInputs({
        invoiceControl,
        isReadOnly,
        fileBeingUploadedName: fileName,
        currency,
        uploadInvoice,
        deleteInvoice,
        downloadInvoice,
      }),
    );
  }, [invoiceControl.status, fileName]);

  useEffect(() => {
    if (invoiceControl.status === INVOICE_STATUS.PENDING && !intervalRef.current) {
      intervalRef.current = setInterval(checkInvoiceControlStatus, 30000);
    }

    if (invoiceControl.status !== INVOICE_STATUS.PENDING && intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [invoiceControl.status]);

  // Check whether form is dirty
  useEffect(() => {
    const isFormDirty = get(form, 'formState.isDirty', false);

    const dirtyFields = Object.keys(get(form, 'formState.dirtyFields', {}));

    const hasAtLeastOneDifferentField = dirtyFields.some(
      // Not being sensitive to type change (case on total property)
      (field) => invoiceControl[field] != formFields[field],
    );

    // Dedicated check on file since it is not detected as dirty
    const hasUploadedFile = !!file;

    if (hasUploadedFile || (isFormDirty && hasAtLeastOneDifferentField)) {
      setIsSaveDisabled(false);

      return;
    }

    setIsSaveDisabled(true);
  }, [formFields]);

  /**
   * Download the file being uploaded
   * @return {Promise<void>}
   */
  const downloadFileBeingUploaded = async () => {
    try {
      await downloadFile(file, fileName);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FILE_DOWNLOAD_ERROR'));
    }
  };

  /**
   * Download the invoice file stored in the CDN and associated with the invoice control
   */
  const downloadInvoiceLink = async () => {
    try {
      const fileName = getFileNameFromUrl(invoiceControl.link);

      await downloadFileFromUrl(invoiceControl.link, fileName);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FILE_DOWNLOAD_ERROR'));
    }
  };

  const getICLByOrderId = async (orderId) => {
    pageLoading();

    try {
      const { invoiceControlList: fetchedInvoiceControlList } =
        await invoiceService.getInvoiceControlListByOrderId(orderId);

      const invoiceControlList = fetchedInvoiceControlList || {};

      setInvoiceControl(invoiceControlList);

      setInputs(
        getInputs({
          invoiceControl,
          currency,
          isReadOnly,
          fileBeingUploadedName: fileName,
          uploadInvoice,
          downloadInvoice,
          deleteInvoice,
        }),
      );

      if (isEmpty(invoiceControlList)) {
        return;
      }

      const invoiceFileName = getFileNameFromUrl(invoiceControlList.link, true);
      setFileName(invoiceFileName);

      form.setValue('fileName', truncateFileNameInTheMiddle(invoiceFileName));
      form.setValue('total', invoiceControlList.total);
      form.setValue('invoiceNumber', invoiceControlList.invoiceNumber);
      form.setValue(
        'invoiceDate',
        invoiceControlList.invoiceDate ? moment(invoiceControlList.invoiceDate) : null,
      );
    } catch {
      setInvoiceControl({});

      showErrorMessage(i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.FETCH_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const checkInvoiceControlStatus = async () => {
    try {
      const { invoiceControlList } = await invoiceService.getInvoiceControlListByOrderId(orderId);

      // If ICL has been deleted in the meantime, we stop the interval by resetting the state
      setInvoiceControl(invoiceControlList || {});

      // Replace values if it has not been set
      if (!formFields.invoiceDate) {
        form.setValue(
          'invoiceDate',
          invoiceControlList.invoiceDate ? moment(invoiceControlList.invoiceDate) : null,
        );
      }

      if (!formFields.total) {
        form.setValue('total', invoiceControlList.total);
      }

      if (!formFields.invoiceNumber) {
        form.setValue('invoiceNumber', invoiceControlList.invoiceNumber);
      }
    } catch {
      showErrorMessage(i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.FETCH_ERROR'));
    }
  };

  const validateForm = async () => {
    const fieldsValidation = await form.trigger();

    setIsSaveAlreadyTriggered(true);

    if (!fieldsValidation) {
      setIsSaveDisabled(true);

      return;
    }

    pageLoading();
    // No try & catch since save method has its own error handling
    await save();
    pageLoaded();
  };

  const save = async (isCheckingInvoice = false) => {
    let link = null;

    if (!!file) {
      const params = {
        clientId,
        keepOriginalFileName: true,
        type: 'invoice-control',
      };

      try {
        const { data } = await uploadService.uploadFile(file, params);

        link = get(data, 'fileUri', null);
      } catch {
        showErrorMessage(i18next.t('ORDERS.ORDERS.FORM_INVOICE_MODAL_IMPORT_FAILURE'));
      }
    }

    const fieldsToUpdate = {
      id: invoiceControl.id || null,
      invoiceDate: formFields.invoiceDate?.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      total: formFields.total,
      invoiceNumber: formFields.invoiceNumber,
      link: encodeFileNameInUrl(link) || invoiceControl.link,
    };

    try {
      const savedInvoiceControlList = await invoiceService.importInvoiceControlListFromOrder(
        clientId,
        supplierId,
        storeId,
        orderId,
        fieldsToUpdate,
      );

      if (isCheckingInvoice) {
        return savedInvoiceControlList;
      }

      showSuccessMessage(i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.UPDATE_SUCCESS'));
      closeModal();
    } catch (err) {
      if (isCheckingInvoice) {
        throw err;
      }

      showErrorMessage(i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.UPDATE_ERROR'));
    }
  };

  const closeModal = () => {
    close();
  };

  return (
    <ModalBackground>
      <ModalContainer>
        <ModalHeader>
          <TitleWithIcon>
            <Icon alt="invoice" src="/images/invoice.svg" />
            <Text font={ENUM_FONTS.H1_INTER}>{i18next.t('ORDERS.ORDERS.FORM_INVOICE_INFOS')}</Text>
          </TitleWithIcon>

          <Icon
            alt="close"
            src="/images/inpulse/close-black-small.svg"
            interactive
            onClick={closeModal}
          />
        </ModalHeader>
        <ModalContent>
          <WhiteCardForm
            form={form}
            inputs={inputs}
            isEditionAllowed={!isReadOnly}
            shouldDisplayError={isSaveAlreadyTriggered}
            title={i18next.t('GENERAL.INFORMATIONS')}
            tooltipText={isReadOnly ? i18next.t('ORDERS.ORDERS.FORM_INVOICE_FORBIDDEN') : ''}
          />
        </ModalContent>
        <ModalFooter>
          <Button
            buttonCustomStyle={{ marginLeft: 24 }}
            color={'inpulse-outline'}
            handleClick={closeModal}
            icon="/images/inpulse/close-black-small.svg"
            label={i18next.t('GENERAL.CANCEL')}
          />
          <Button
            buttonCustomStyle={{ marginLeft: 24 }}
            color={'inpulse-default'}
            handleClick={validateForm}
            icon="/images/inpulse/save-white-small.svg"
            isDisabled={isSaveDisabled}
            label={i18next.t('GENERAL.SAVE')}
          />
        </ModalFooter>
      </ModalContainer>
    </ModalBackground>
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  client: getClientInfo(state.baseReducer.user),
  authorizedActions: getAuthorizedActions(state.baseReducer.userRights, '/order/orders'),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliveryInvoiceModal);
